// Phase 84.M.101 — Reset Password page (FE).
//
// Đọc `?token=<xxx>` từ URL → user nhập password mới + confirm → POST
// /api/auth/reset-password. BE validate token expires/used.
import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Lock, ArrowLeft, CheckCircle } from "@phosphor-icons/react";
import { authApi, formatApiErrorDetail } from "../lib/authApi";
import Logo from "../components/common/Logo";
import BackButton from "../components/common/BackButton";

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) {
      toast.error("Token không hợp lệ. Quay lại đăng nhập.");
    }
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    if (!token) {
      toast.error("Token không hợp lệ");
      return;
    }
    if (password.length < 8) {
      toast.error("Mật khẩu mới phải ít nhất 8 ký tự");
      return;
    }
    if (password !== confirm) {
      toast.error("Xác nhận mật khẩu không khớp");
      return;
    }
    setBusy(true);
    try {
      await authApi.resetPassword(token, password);
      setDone(true);
      toast.success("Đặt lại mật khẩu thành công");
      setTimeout(() => navigate("/login"), 1500);
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err?.response?.data?.detail) ||
          "Có lỗi xảy ra, vui lòng thử lại",
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#0B1220] via-[#0F1A2E] to-[#10162B] px-4 py-10"
      data-testid="reset-password-page"
    >
      <BackButton testId="reset-back" />
      <div className="w-full max-w-md">
        <div className="flex justify-center mb-6">
          <Logo size={64} />
        </div>
        <div className="bg-gradient-to-br from-[#0F1A2E] to-[#0B1220] border border-white/10 rounded-3xl shadow-2xl p-6 sm:p-8">
          <div className="mb-6">
            <button
              type="button"
              onClick={() => navigate("/login")}
              className="text-[12px] text-white/55 hover:text-white inline-flex items-center gap-1.5 mb-3"
              data-testid="reset-back-btn"
            >
              <ArrowLeft size={12} weight="bold" />
              Quay lại đăng nhập
            </button>
            <h1 className="text-2xl font-black text-white tracking-tight">
              Đặt lại mật khẩu
            </h1>
            <p className="text-[13px] text-white/65 mt-1.5 leading-relaxed">
              Nhập mật khẩu mới cho tài khoản của bạn. Link có hiệu lực trong 1 giờ.
            </p>
          </div>

          {done ? (
            <div
              className="rounded-2xl border border-emerald-400/30 bg-emerald-500/8 p-4 text-[13px] text-emerald-100 leading-relaxed text-center"
              data-testid="reset-success-msg"
            >
              <CheckCircle size={32} weight="fill" className="text-emerald-300 mx-auto mb-2" />
              <div className="font-black mb-1">Đặt lại thành công!</div>
              <p className="text-[12px]">Đang chuyển hướng về trang đăng nhập…</p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <PasswordField
                label="Mật khẩu mới"
                value={password}
                onChange={setPassword}
                autoFocus
                testId="reset-password-input"
                disabled={busy}
              />
              <PasswordField
                label="Xác nhận mật khẩu"
                value={confirm}
                onChange={setConfirm}
                testId="reset-confirm-input"
                disabled={busy}
              />
              <button
                type="submit"
                disabled={busy || !token}
                className="w-full h-12 rounded-2xl bg-gradient-to-r from-[#FF8A00] to-[#FF9F1C] text-white text-[13px] font-black tracking-wide shadow-[0_8px_24px_rgba(255,138,0,0.45)] disabled:opacity-40 inline-flex items-center justify-center gap-2"
                data-testid="reset-submit-btn"
              >
                {busy ? "Đang đặt lại…" : "Đặt lại mật khẩu"}
              </button>
              <div className="text-center pt-1">
                <Link
                  to="/login"
                  className="text-[12px] text-white/55 hover:text-white"
                  data-testid="reset-login-link"
                >
                  Quay lại <span className="text-white">đăng nhập</span>
                </Link>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

function PasswordField({ label, value, onChange, autoFocus = false, testId, disabled }) {
  return (
    <label className="block">
      <span className="block text-[10.5px] uppercase tracking-widest text-white/55 font-black mb-1.5">
        {label}
      </span>
      <div className="relative">
        <Lock
          size={16}
          weight="bold"
          className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40"
        />
        <input
          type="password"
          autoComplete="new-password"
          autoFocus={autoFocus}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Tối thiểu 8 ký tự"
          className="w-full h-12 pl-9 pr-4 rounded-xl bg-[#0F1623] border border-white/15 text-white text-[14px] outline-none focus:border-[#FF8A00]/60"
          data-testid={testId}
          disabled={disabled}
        />
      </div>
    </label>
  );
}
